import {
    CheckCircleIcon,
    ExclamationTriangleIcon,
    QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import type { RiskStatus } from '@/utils/completionDateCalculation';

interface StatusIndicatorProps {
    status: RiskStatus;
    label?: string;
    className?: string;
}

const STATUS_CONFIG: Record<
    RiskStatus,
    { label: string; icon: React.ComponentType<{ className?: string }>; className: string; ariaLabel: string }
> = {
    on_track: {
        label: 'On Track',
        icon: CheckCircleIcon,
        className: 'bg-success-600 text-white',
        ariaLabel: 'Project is on track to meet deadline',
    },
    at_risk: {
        label: 'At Risk',
        icon: ExclamationTriangleIcon,
        className: 'bg-warning-500 text-white',
        ariaLabel: 'Warning: Project is at risk of missing deadline',
    },
    overdue: {
        label: 'Overdue',
        icon: ExclamationTriangleIcon,
        className: 'bg-danger-600 text-white',
        ariaLabel: 'Warning: This project is overdue and has missed its deadline',
    },
    unknown: {
        label: 'Unknown',
        icon: QuestionMarkCircleIcon,
        className: 'bg-gray-400 text-white',
        ariaLabel: 'Project status unknown',
    },
};

export const StatusIndicator = ({ status, label, className = '' }: StatusIndicatorProps) => {
    const config = STATUS_CONFIG[status];
    const Icon = config.icon;
    const displayLabel = label ?? config.label;
    const ariaLabel = label ? `Project status: ${label}` : config.ariaLabel;

    return (
        <span
            role="status"
            aria-label={ariaLabel}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${config.className} ${className}`}
        >
            <Icon className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
            <span>{displayLabel}</span>
        </span>
    );
};
